<template>
  <div class="api-docs">
    <div class="container">
      <div class="docs-header">
        <h1 class="docs-title">{{ $t('apiDocs.title') }}</h1>
        <p class="docs-subtitle">{{ $t('apiDocs.subtitle') }}</p>
      </div>

      <div class="docs-card card">
        <!-- 注意事项部分 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.noticeTitle') }}</h2>
          <div class="notice-box">
            <p><strong>{{ $t('apiDocs.noticeText') }}</strong></p>
            <ul>
              <!-- <li>API域名地址为: <code>https://api.qc86.shop</code></li> -->
              <li>{{ $t('apiDocs.noticeItems.0') }}</li>
              <li>{{ $t('apiDocs.noticeItems.1') }}</li>
              <li>{{ $t('apiDocs.noticeItems.2') }}</li>
              <li>{{ $t('apiDocs.noticeItems.3') }}</li>
              <li>{{ $t('apiDocs.noticeItems.4') }}</li>
              <li>{{ $t('apiDocs.noticeItems.5') }}</li>
              <li>{{ $t('apiDocs.noticeItems.6') }}</li>
            </ul>
          </div>
        </div>

        <!-- API访问配置 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiCredentials.accessTitle') || 'API访问配置' }}</h2>
          <div class="credentials-container">
            <div class="notice-box">
              <p><strong>{{ $t('apiDocs.apiCredentials.description') || '您可以使用API认证凭证来访问我们的API服务，请妥善保管您的凭证信息。' }}</strong></p>
            </div>

            <div class="access-settings-grid">
            <section class="access-panel whitelist-form" aria-labelledby="whitelist-title">
              <div class="panel-heading">
                <div>
                  <h3 id="whitelist-title">{{ $t('apiDocs.apiCredentials.whitelistTitle') || 'IP白名单' }}</h3>
                  <p>{{ $t('apiDocs.apiCredentials.whitelistDescription') || '仅允许保存的IP使用API凭证登录。' }}</p>
                </div>
                <span class="status-badge">{{ $t('apiDocs.apiCredentials.securityEnabled') || '安全防护已启用' }}</span>
              </div>
              <div class="current-ip-row">
                <div>
                  <span class="field-caption">{{ $t('apiDocs.apiCredentials.currentIpTitle') || '当前IP' }}</span>
                  <code>{{ loadingCurrentIp ? ($t('apiDocs.apiCredentials.gettingCurrentIp') || '获取中...') : (currentIp || '--') }}</code>
                </div>
                <button class="btn btn-secondary" type="button" @click="addCurrentIp" :disabled="loadingCurrentIp || loadingWhitelist">
                  {{ $t('apiDocs.apiCredentials.addCurrentIp') || '添加当前IP' }}
                </button>
              </div>
              <label for="api-ip-whitelist">{{ $t('apiDocs.apiCredentials.apiIpWhitelist') || '允许登录的IP' }}</label>
              <textarea
                id="api-ip-whitelist"
                v-model="apiIpWhitelist"
                :placeholder="$t('apiDocs.apiCredentials.apiIpWhitelistPlaceholder') || '请输入允许API登录的IP，多个IP用逗号分隔'"
                :disabled="loadingWhitelist"
                :aria-invalid="Boolean(whitelistError)"
                :aria-describedby="whitelistError ? 'whitelist-error' : 'whitelist-tip'"
                rows="4"
              ></textarea>
              <p v-if="whitelistError" id="whitelist-error" class="field-error" role="alert">{{ whitelistError }}</p>
              <p v-else id="whitelist-tip">{{ $t('apiDocs.apiCredentials.apiIpWhitelistTip') || '多个IP可使用逗号、空格或换行分隔。' }}</p>
              <div class="whitelist-actions">
                <button class="btn btn-primary" type="button" @click="saveApiIpWhitelist" :disabled="loadingWhitelist">
                  {{ loadingWhitelist ? ($t('apiDocs.apiCredentials.savingWhitelist') || '保存中...') : ($t('apiDocs.apiCredentials.saveWhitelist') || '保存白名单') }}
                </button>
              </div>
            </section>

            <section class="access-panel credentials-panel" aria-labelledby="credentials-title">
              <div class="panel-heading">
                <div>
                  <h3 id="credentials-title">{{ $t('apiDocs.apiCredentials.title') || 'API认证凭证' }}</h3>
                  <p>{{ $t('apiDocs.apiCredentials.credentialsDescription') || '查看或生成API登录用户名和密码。' }}</p>
                </div>
              </div>

            <div class="credentials-actions" v-if="!apiCredentials">
              <button class="btn btn-primary" @click="fetchApiCredentials" :disabled="loadingCredentials">
                <span v-if="!loadingCredentials">{{ $t('apiDocs.apiCredentials.getCredentials') || '获取API凭证' }}</span>
                <span v-else>{{ $t('apiDocs.apiCredentials.loading') || '加载中...' }}</span>
              </button>
            </div>

            <div class="credentials-display" v-if="apiCredentials">
              <div class="credential-item">
                <label>{{ $t('apiDocs.apiCredentials.apiUsername') || 'API Username' }}:</label>
                <div class="credential-value">
                  <code>{{ apiCredentials.apiUsername }}</code>
                  <button class="btn-copy" @click="copyToClipboard(apiCredentials.apiUsername, 'API Username')" title="复制">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div class="credential-item">
                <label>{{ $t('apiDocs.apiCredentials.apiPassword') || 'API Password' }}:</label>
                <div class="credential-value">
                  <code>{{ showSecret ? apiCredentials.apiPassword : '••••••••••••••••••••••••••••••••' }}</code>
                  <button class="btn-toggle" @click="showSecret = !showSecret" :title="showSecret ? '隐藏' : '显示'">
                    <svg v-if="!showSecret" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06L17.94 17.94Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19L9.9 4.24Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button class="btn-copy" @click="copyToClipboard(apiCredentials.apiPassword, 'API Password')" title="复制">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div class="api-notes warning">
                <strong>{{ $t('apiDocs.apiCredentials.securityWarning') || '安全提示' }}:</strong>
                <p>{{ $t('apiDocs.apiCredentials.securityMessage') || '请妥善保管您的API凭证，不要与他人分享。如果凭证泄露，请联系客服处理。' }}</p>
              </div>

              <div class="danger-zone">
                <div>
                  <strong>{{ $t('apiDocs.apiCredentials.dangerTitle') || '重置API凭证' }}</strong>
                  <p>{{ $t('apiDocs.apiCredentials.dangerDescription') || '旧凭证会立即失效，并退出当前登录。' }}</p>
                </div>
                <button class="btn btn-danger" @click="confirmResetCredentials" :disabled="loadingCredentials">
                  {{ $t('apiDocs.apiCredentials.resetCredentials') || '重置凭证' }}
                </button>
              </div>
            </div>
            </section>
            </div>
          </div>
        </div>

        <!-- API认证说明 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.authentication') || 'API认证' }}</h2>
          <div class="api-details">
            <div class="api-notes">
              <strong>{{ $t('apiDocs.authenticationInstructions.title') || '关于API认证说明：' }}</strong>
              <p>{{ $t('apiDocs.authenticationInstructions.description') || '本平台使用API Username和API Password进行身份认证。您需要先在上方"API认证凭证"部分获取您的凭证信息。' }}</p>
              <ul>
                <li>{{ $t('apiDocs.authenticationInstructions.step1') || '1. 点击"获取API凭证"按钮获取您的 API Username 和 API Password' }}</li>
                <li>{{ $t('apiDocs.authenticationInstructions.step2') || '2. 使用API Username和API Password调用登录接口获取token' }}</li>
                <li>{{ $t('apiDocs.authenticationInstructions.step3') || '3. 使用获取到的token调用其他API接口' }}</li>
                <li>{{ $t('apiDocs.authenticationInstructions.step4') || '4. token永久有效，只需登录一次即可' }}</li>
                <li>{{ $t('apiDocs.authenticationInstructions.step5') || '5. 重置凭证后旧凭证将立即失效，需要重新登录获取新token' }}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 登录获取token -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.login') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET</span>
              <span class="endpoint">https://api.qc86.shop/api/login</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>{{ $t('apiDocs.tableHeaders.param') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.required') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.type') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>username</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.apiCredentials.apiUsername') || 'API Username' }}</td>
                  </tr>
                  <tr>
                    <td>password</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.apiCredentials.apiPassword') || 'API Password' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/login?username=your_api_username&password=your_api_password</code></pre>
            </div>

            <h3>{{ $t('apiDocs.responseExample') }}</h3>
            <div class="code-block">
              <pre><code>{"data":{"admin":false,"avatar":null,"deptName":"组织机构","id":4064,"name":"ce1235","resources":{"/msg/user":217,"/user":3},"roleName":"默认管理员","token":"4064-*-46e7a4cc-65f4-4b69-b98b-655f258fcce4"},"msg":"操作成功","status":200,"success":true,"t":null}</code></pre>
            </div>

            <div class="api-notes">
              <strong>{{ $t('apiDocs.authenticationInstructions.tokenNote') || '关于token说明：' }}</strong>
              <p>{{ $t('apiDocs.authenticationInstructions.tokenMessage') || '使用API凭证登录接口只需要调用一次，获取到的token永久有效。token代表了您的API身份，请妥善保管。重置API凭证后，需要使用新的API凭证重新登录获取新token。' }}</p>
            </div>
          </div>
        </div>

        <!-- 获取用户余额 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.getBalance') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET</span>
              <span class="endpoint">https://api.qc86.shop/api/getWallet</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/getWallet?token=xxx</code></pre>
            </div>

            <h3>{{ $t('apiDocs.responseExample') }}</h3>
            <div class="code-block">
              <pre><code>{"data":{"balances":"10.00","createTime":"2023-05-18 18:12:24","id":9117,"uid":4064,"updateTime":"2023-05-18 18:12:24"},"msg":"操作成功","status":200,"success":true,"t":"2620f57d-e6ce-4ddd-b1a3-8ea2993d8efd"}</code></pre>
            </div>
          </div>
        </div>

        <!-- 获取临时号码 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.getPhone') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET</span>
              <span class="endpoint">https://api.qc86.shop/api/getPhone</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>{{ $t('apiDocs.tableHeaders.param') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.required') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.type') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>token</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('common.default') }}</td>
                  </tr>
                  <tr>
                    <td>phoneNum</td>
                    <td>false</td>
                    <td>string</td>
                    <td>{{ $t('advancedOptions.specificPhone') }}</td>
                  </tr>
                  <tr>
                    <td>operator</td>
                    <td>false</td>
                    <td>string</td>
                    <td>0=全部，5=虚拟，4=非虚拟</td>
                  </tr>
                  <tr>
                    <td>scope</td>
                    <td>false</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.paramDescriptions.scope') }}</td>
                  </tr>
                  <tr>
                    <td>scope_black</td>
                    <td>false</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.paramDescriptions.scopeBlack') }}</td>
                  </tr>
                  <tr>
                    <td>channelId</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.paramDescriptions.channelId') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/getPhone?token=xxxx&channelId=1237436366606831616</code></pre>
            </div>

            <h3>{{ $t('apiDocs.responseExample') }}</h3>
            <div class="code-block">
              <pre><code>{"data":{"code":0,"desc":null,"lastMsgTime":null,"message":"获取临时号码成功","minute":null,"mobile":"15664864435","phoneId":null,"refreshTime":5000,"smsTask":{"createTime":null,"endTime":null,"id":1234789,"phoneNo":"15664864435","projectId":89371,"startTime":"2023-06-08 21:17:18","status":0,"uid":4064,"updateTime":null}},"msg":"操作成功","status":200,"success":true,"t":"9fb21055-33c6-4189-a995-165e80a0ac5f"}</code></pre>
            </div>
          </div>
        </div>

        <!-- 获取共享数据 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.getCode') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET</span>
              <span class="endpoint">https://api.qc86.shop/api/getCode</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>{{ $t('apiDocs.tableHeaders.param') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.required') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.type') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>token</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('common.default') }}</td>
                  </tr>
                  <tr>
                    <td>channelId</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.paramDescriptions.channelId') }}</td>
                  </tr>
                  <tr>
                    <td>phoneNum</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('phoneList.headers.phoneNumber') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/getCode?token=4064-*-e3751f51-b5fe-4d05-abca-8a824ef4e6c1&channelId=1237436366606831616&phoneNum=15667173485</code></pre>
            </div>

            <h3>{{ $t('apiDocs.responseExample') }}</h3>
            <div class="code-block">
              <pre><code>{"data":{"code":"893134","message":"ok","modle":"【京东】请确认本人操作，切勿泄露给他人。您正在新设备上登录，京东工作人员不会向您索取共享数据，共享数据：893134。如非本人操作请修改密码。"},"msg":"操作成功","status":200,"success":true,"t":"ab094a9c-8f53-4df2-9acb-8271437e0c9b"}</code></pre>
            </div>
          </div>
        </div>

        <!-- 邮箱接码 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.mailReceive') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET / POST</span>
              <span class="endpoint">https://api.qc86.shop/api/mailActivate</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>{{ $t('apiDocs.tableHeaders.param') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.required') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.type') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>token</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.mailApi.token') }}</td>
                  </tr>
                  <tr>
                    <td>serviceCode</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.mailApi.serviceCode') }}</td>
                  </tr>
                  <tr>
                    <td>domain</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.mailApi.domain') }}</td>
                  </tr>
                  <tr>
                    <td>supplier</td>
                    <td>false</td>
                    <td>int</td>
                    <td>{{ $t('apiDocs.mailApi.supplier') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/mailActivate?token=xxx&serviceCode=ig&domain=gmail.com</code></pre>
            </div>

            <h3>{{ $t('apiDocs.responseExample') }}</h3>
            <div class="code-block">
              <pre><code>{"data":{"activationId":123,"mailAddress":"example@gmail.com","userPrice":1.20,"status":"WAITING_CODE","expireTime":"2026-10-20 12:15:00"},"success":true}</code></pre>
            </div>

            <div class="api-method">
              <span class="method method-get">GET / POST</span>
              <span class="endpoint">https://api.qc86.shop/api/mailCode</span>
            </div>
            <p>{{ $t('apiDocs.mailApi.codeDescription') }}</p>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/mailCode?token=xxx&activationId=123</code></pre>
            </div>

            <div class="api-method">
              <span class="method method-get">GET / POST</span>
              <span class="endpoint">https://api.qc86.shop/api/mailRelease</span>
            </div>
            <p>{{ $t('apiDocs.mailApi.releaseDescription') }}</p>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/mailRelease?token=xxx&activationId=123</code></pre>
            </div>
          </div>
        </div>

        <!-- 屏蔽临时号码 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.blacklistPhone') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET</span>
              <span class="endpoint">https://api.qc86.shop/api/phoneCollectAdd</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>{{ $t('apiDocs.tableHeaders.param') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.required') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.type') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>token</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('common.default') }}</td>
                  </tr>
                  <tr>
                    <td>channelId</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.paramDescriptions.channelId') }}</td>
                  </tr>
                  <tr>
                    <td>phoneNo</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('phoneList.headers.phoneNumber') }}</td>
                  </tr>
                  <tr>
                    <td>type</td>
                    <td>true</td>
                    <td>int</td>
                    <td>{{ $t('common.default') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/phoneCollectAdd?token=xxx&channelId=1237436366606831616&phoneNo=15667173485&type=0</code></pre>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }} (POST)</h3>
            <div class="code-block">
              <pre><code>{
    "channelId": "1111111111111111111111",
    "phoneNo": "",
    "type": 0
}</code></pre>
            </div>
          </div>
        </div>

        <!-- 结束共享 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.apiSections.releasePhone') }}</h2>
          <div class="api-details">
            <div class="api-method">
              <span class="method method-get">GET</span>
              <span class="endpoint">https://api.qc86.shop/api/release</span>
            </div>

            <h3>{{ $t('apiDocs.requestParams') }}</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>{{ $t('apiDocs.tableHeaders.param') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.required') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.type') }}</th>
                    <th>{{ $t('apiDocs.tableHeaders.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>token</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('common.default') }}</td>
                  </tr>
                  <tr>
                    <td>channelId</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('apiDocs.paramDescriptions.channelId') }}</td>
                  </tr>
                  <tr>
                    <td>phoneNo</td>
                    <td>true</td>
                    <td>string</td>
                    <td>{{ $t('phoneList.headers.phoneNumber') }}</td>
                  </tr>
                  <tr>
                    <td>status</td>
                    <td>true</td>
                    <td>int</td>
                    <td>{{ $t('common.default') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{{ $t('apiDocs.requestExample') }}</h3>
            <div class="code-block">
              <pre><code>https://api.qc86.shop/api/release?token=xxx&channelId=1525913078447738880&phoneNo=19232536467&status=2</code></pre>
            </div>
          </div>
        </div>

        <!-- 接入示例代码部分 -->
        <div class="docs-section">
          <h2>{{ $t('apiDocs.exampleCode') }}</h2>
          <div class="api-details">
            <div class="code-tabs">
              <div class="tab-header">
                <button 
                  class="tab-btn" 
                  :class="{ active: activeCodeTab === 'python' }" 
                  @click="activeCodeTab = 'python'"
                >
{{ $t('apiDocs.languageTabs.python') }}
                </button>
                <button 
                  class="tab-btn" 
                  :class="{ active: activeCodeTab === 'java' }" 
                  @click="activeCodeTab = 'java'"
                >
{{ $t('apiDocs.languageTabs.java') }}
                </button>
              </div>
              
              <div class="tab-content" v-show="activeCodeTab === 'python'">
                <div class="code-block code-block-large">
                  <pre><code>import requests
import time

# API 基础URL
BASE_URL = "https://api.qc86.shop/api"

# API凭证（从网站"API认证凭证"部分获取）
API_USERNAME = "your_api_username"  # 替换为您的API Username
API_PASSWORD = "your_api_password"  # 替换为您的API Password

# 获取Token
def get_token():
    url = f"{BASE_URL}/login"
    params = {
        "username": API_USERNAME,
        "password": API_PASSWORD
    }

    response = requests.get(url, params=params)
    data = response.json()

    if data["success"]:
        return data["data"]["token"]
    else:
        raise Exception(f"登录失败: {data['msg']}")

# 获取余额
def get_wallet(token):
    url = f"{BASE_URL}/getWallet"
    params = {"token": token}

    response = requests.get(url, params=params)
    return response.json()

# 获取临时号码
def get_phone(token, channel_id, operator=0, scope=None):
    url = f"{BASE_URL}/getPhone"
    params = {
        "token": token,
        "channelId": channel_id,
        "operator": operator
    }

    if scope:
        params["scope"] = scope

    response = requests.get(url, params=params)
    return response.json()

# 获取共享数据
def get_code(token, channel_id, phone_num):
    url = f"{BASE_URL}/getCode"
    params = {
        "token": token,
        "channelId": channel_id,
        "phoneNum": phone_num
    }

    # 尝试最多获取100次共享数据
    for _ in range(100):
        response = requests.get(url, params=params)
        data = response.json()

        if data["success"] and data["data"]["code"]:
            return data

        # 等待1秒后再次尝试
        time.sleep(1)

    return None

# 屏蔽临时号码
def blacklist_phone(token, channel_id, phone_no):
    url = f"{BASE_URL}/phoneCollectAdd"
    params = {
        "token": token,
        "channelId": channel_id,
        "phoneNo": phone_no,
        "type": 0
    }

    response = requests.get(url, params=params)
    return response.json()

# 结束共享
def release_phone(token, channel_id, phone_no):
    url = f"{BASE_URL}/release"
    params = {
        "token": token,
        "channelId": channel_id,
        "phoneNo": phone_no,
        "status": 2
    }
    response = requests.get(url, params=params)
    return response.json()

# 使用示例
def main():
    try:
        # 1. 使用API凭证登录获取token
        token = get_token()
        print(f"获取到token: {token}")

        # 2. 获取用户余额
        wallet = get_wallet(token)
        print(f"当前余额: {wallet['data']['balances']}")

        # 3. 获取临时号码
        channel_id = "1237436366606831616"  # 替换为实际渠道ID
        phone_result = get_phone(token, channel_id)

        if phone_result["success"]:
            phone_num = phone_result["data"]["mobile"]
            print(f"获取到临时号码: {phone_num}")

            # 4. 获取共享数据
            code_result = get_code(token, channel_id, phone_num)

            if code_result and code_result["success"]:
                code = code_result["data"]["code"]
                message = code_result["data"]["modle"]
                print(f"获取到共享数据: {code}")
                print(f"完整数据消息: {message}")
            else:
                print("未获取到共享数据")
                # 如果未获取到共享数据，屏蔽临时号码
                blacklist_phone(token, channel_id, phone_num)
                print(f"已将临时号码 {phone_num} 加入屏蔽列表")
        else:
            print(f"获取临时号码失败: {phone_result['msg']}")

    except Exception as e:
        print(f"发生错误: {e}")

if __name__ == "__main__":
    main()</code></pre>
                </div>
              </div>
              
              <div class="tab-content" v-show="activeCodeTab === 'java'">
                <div class="code-block code-block-large">
                  <pre><code>import java.io.BufferedReader;
import java.io.InputStreamReader;
import java.net.HttpURLConnection;
import java.net.URL;
import java.net.URLEncoder;
import java.nio.charset.StandardCharsets;
import java.util.HashMap;
import java.util.Map;
import org.json.JSONObject;

public class QianChuanSmsClient {
    private static final String BASE_URL = "https://api.qc86.shop/api";
    private String token;
    private final String apiUsername;  // API Username (从网站获取)
    private final String apiPassword;  // API Password (从网站获取)

    public QianChuanSmsClient(String apiUsername, String apiPassword) {
        this.apiUsername = apiUsername;
        this.apiPassword = apiPassword;
    }

    // 登录获取token
    public boolean login() throws Exception {
        String urlStr = BASE_URL + "/login";
        Map&lt;String, String&gt; params = new HashMap&lt;&gt;();
        params.put("username", apiUsername);
        params.put("password", apiPassword);

        JSONObject response = sendGetRequest(urlStr, params);
        if (response.getBoolean("success")) {
            this.token = response.getJSONObject("data").getString("token");
            return true;
        } else {
            System.err.println("登录失败: " + response.getString("msg"));
            return false;
        }
    }

    // 获取用户余额
    public JSONObject getWallet() throws Exception {
        checkToken();
        String urlStr = BASE_URL + "/getWallet";
        Map&lt;String, String&gt; params = new HashMap&lt;&gt;();
        params.put("token", token);

        return sendGetRequest(urlStr, params);
    }

    // 获取临时号码
    public JSONObject getPhone(String channelId, String operator, String scope) throws Exception {
        checkToken();
        String urlStr = BASE_URL + "/getPhone";
        Map&lt;String, String&gt; params = new HashMap&lt;&gt;();
        params.put("token", token);
        params.put("channelId", channelId);

        if (operator != null) {
            params.put("operator", operator);
        }

        if (scope != null) {
            params.put("scope", scope);
        }

        return sendGetRequest(urlStr, params);
    }

    // 获取共享数据
    public JSONObject getCode(String channelId, String phoneNum) throws Exception {
        checkToken();
        String urlStr = BASE_URL + "/getCode";
        Map&lt;String, String&gt; params = new HashMap&lt;&gt;();
        params.put("token", token);
        params.put("channelId", channelId);
        params.put("phoneNum", phoneNum);

        // 尝试最多获取100次共享数据
        for (int i = 0; i &lt; 100; i++) {
            JSONObject response = sendGetRequest(urlStr, params);

            if (response.getBoolean("success") &&
                response.has("data") &&
                response.getJSONObject("data").has("code") &&
                !response.getJSONObject("data").getString("code").isEmpty()) {
                return response;
            }

            // 等待1秒后再次尝试
            Thread.sleep(1000);
        }

        // 如果未获取到共享数据，返回null
        return null;
    }

    // 屏蔽临时号码
    public JSONObject blacklistPhone(String channelId, String phoneNo) throws Exception {
        checkToken();
        String urlStr = BASE_URL + "/phoneCollectAdd";
        Map&lt;String, String&gt; params = new HashMap&lt;&gt;();
        params.put("token", token);
        params.put("channelId", channelId);
        params.put("phoneNo", phoneNo);
        params.put("type", "0");

        return sendGetRequest(urlStr, params);
    }

    // 结束共享
    public JSONObject releasePhone(String channelId, String phoneNo) throws Exception {
        checkToken();
        String urlStr = BASE_URL + "/release";
        Map&lt;String, String&gt; params = new HashMap&lt;&gt;();
        params.put("token", token);
        params.put("channelId", channelId);
        params.put("phoneNo", phoneNo);
        params.put("status", "2");

        return sendGetRequest(urlStr, params);
    }

    // 检查token是否存在
    private void checkToken() throws Exception {
        if (token == null || token.isEmpty()) {
            if (!login()) {
                throw new Exception("未登录或登录失败");
            }
        }
    }

    // 发送GET请求
    private JSONObject sendGetRequest(String urlStr, Map&lt;String, String&gt; params) throws Exception {
        StringBuilder urlBuilder = new StringBuilder(urlStr);

        if (!params.isEmpty()) {
            urlBuilder.append("?");
            boolean first = true;

            for (Map.Entry&lt;String, String&gt; entry : params.entrySet()) {
                if (!first) {
                    urlBuilder.append("&");
                }
                urlBuilder.append(URLEncoder.encode(entry.getKey(), StandardCharsets.UTF_8.toString()));
                urlBuilder.append("=");
                urlBuilder.append(URLEncoder.encode(entry.getValue(), StandardCharsets.UTF_8.toString()));
                first = false;
            }
        }

        URL url = new URL(urlBuilder.toString());
        HttpURLConnection conn = (HttpURLConnection) url.openConnection();
        conn.setRequestMethod("GET");
        conn.setRequestProperty("Accept", "application/json");

        if (conn.getResponseCode() != 200) {
            throw new RuntimeException("HTTP错误: " + conn.getResponseCode());
        }

        BufferedReader br = new BufferedReader(new InputStreamReader(conn.getInputStream()));
        StringBuilder response = new StringBuilder();
        String line;

        while ((line = br.readLine()) != null) {
            response.append(line);
        }

        br.close();
        conn.disconnect();

        return new JSONObject(response.toString());
    }

    // 使用示例
    public static void main(String[] args) {
        try {
            // 使用API凭证（从网站"API认证凭证"部分获取）
            QianChuanSmsClient client = new QianChuanSmsClient(
                "your_api_username",  // 替换为您的API Username
                "your_api_password"   // 替换为您的API Password
            );

            // 登录
            if (client.login()) {
                System.out.println("登录成功");

                // 获取余额
                JSONObject wallet = client.getWallet();
                System.out.println("当前余额: " + wallet.getJSONObject("data").getString("balances"));

                // 获取临时号码
                String channelId = "1237436366606831616"; // 替换为实际渠道ID
                JSONObject phoneResult = client.getPhone(channelId, null, null);

                if (phoneResult.getBoolean("success")) {
                    String phoneNum = phoneResult.getJSONObject("data").getString("mobile");
                    System.out.println("获取到临时号码: " + phoneNum);

                    // 获取共享数据
                    JSONObject codeResult = client.getCode(channelId, phoneNum);

                    if (codeResult != null && codeResult.getBoolean("success")) {
                        String code = codeResult.getJSONObject("data").getString("code");
                        String message = codeResult.getJSONObject("data").getString("modle");
                        System.out.println("获取到共享数据: " + code);
                        System.out.println("完整数据消息: " + message);
                    } else {
                        System.out.println("未获取到共享数据");
                        // 如果未获取到共享数据，屏蔽临时号码
                        client.blacklistPhone(channelId, phoneNum);
                        System.out.println("已将临时号码 " + phoneNum + " 加入屏蔽列表");
                    }
                } else {
                    System.out.println("获取临时号码失败: " + phoneResult.getString("msg"));
                }
            }
        } catch (Exception e) {
            System.err.println("发生错误: " + e.getMessage());
            e.printStackTrace();
        }
    }
}</code></pre>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {
  getApiCredentials,
  resetApiCredentials,
  getApiIpWhitelist,
  updateApiIpWhitelist,
  getCurrentIp,
  logout
} from '@/api/user'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'ApiDocsView',
  data() {
    return {
      activeCodeTab: 'python',
      apiCredentials: null,
      apiIpWhitelist: '',
      currentIp: '',
      whitelistError: '',
      loadingWhitelist: false,
      loadingCredentials: false,
      loadingCurrentIp: false,
      showSecret: false
    }
  },
  created() {
    // 检查用户是否已登录
    const token = localStorage.getItem('tianqi-token')
    if (!token) {
      this.$router.push('/login')
    }
  },
  mounted() {
    this.loadApiIpWhitelist()
    this.loadCurrentIp()
  },
  methods: {
    validateApiIpWhitelist() {
      if (!this.apiIpWhitelist || !this.apiIpWhitelist.trim()) {
        this.whitelistError = this.$t('apiDocs.apiCredentials.apiIpWhitelistRequired') || '请填写API登录IP白名单'
        return false
      }
      this.whitelistError = ''
      return true
    },
    parseIpList(value) {
      return value.split(/[,;\s]+/).map(item => item.trim()).filter(Boolean)
    },
    async loadApiIpWhitelist() {
      this.loadingWhitelist = true
      try {
        const response = await getApiIpWhitelist()
        this.apiIpWhitelist = response.data?.apiIpWhitelist || ''
      } catch (error) {
        console.error('获取API登录IP白名单失败:', error)
      } finally {
        this.loadingWhitelist = false
      }
    },
    async loadCurrentIp() {
      this.loadingCurrentIp = true
      try {
        const response = await getCurrentIp()
        this.currentIp = response.data || ''
      } catch (error) {
        console.error('获取当前IP失败:', error)
      } finally {
        this.loadingCurrentIp = false
      }
    },
    async addCurrentIp() {
      if (!this.currentIp) await this.loadCurrentIp()
      if (!this.currentIp) {
        ElMessage.error(this.$t('apiDocs.apiCredentials.currentIpEmpty') || '未获取到当前IP')
        return
      }
      const ips = this.parseIpList(this.apiIpWhitelist)
      if (!ips.includes(this.currentIp)) ips.push(this.currentIp)
      this.apiIpWhitelist = ips.join(', ')
      this.whitelistError = ''
    },
    async saveApiIpWhitelist() {
      if (!this.validateApiIpWhitelist()) return
      this.loadingWhitelist = true
      try {
        const response = await updateApiIpWhitelist({
          apiIpWhitelist: this.apiIpWhitelist.trim(),
          apiIpWhitelistEnabled: true
        })
        this.apiIpWhitelist = response.data?.apiIpWhitelist || this.apiIpWhitelist
        ElMessage.success(this.$t('apiDocs.apiCredentials.whitelistSaved') || 'IP白名单已保存')
      } catch (error) {
        this.whitelistError = error.message || (this.$t('apiDocs.apiCredentials.whitelistSaveError') || 'IP白名单保存失败')
      } finally {
        this.loadingWhitelist = false
      }
    },
    async fetchApiCredentials() {
      this.loadingCredentials = true
      try {
        const response = await getApiCredentials()
        if (response.success) {
          this.apiCredentials = response.data
          ElMessage.success('API凭证获取成功')
        } else {
          ElMessage.error(response.msg || '获取API凭证失败')
        }
      } catch (error) {
        console.error('获取API凭证失败:', error)
        ElMessage.error('获取API凭证失败，请稍后重试')
      } finally {
        this.loadingCredentials = false
      }
    },
    confirmResetCredentials() {
      ElMessageBox.confirm(
        this.$t('apiDocs.apiCredentials.resetConfirmMessage') || '重置凭证后，旧的凭证将立即失效，并且您将被强制退出登录。确定要继续吗？',
        this.$t('apiDocs.apiCredentials.resetConfirmTitle') || '确认重置凭证',
        {
          confirmButtonText: this.$t('common.confirm') || '确认',
          cancelButtonText: this.$t('common.cancel') || '取消',
          type: 'warning',
          dangerouslyUseHTMLString: false
        }
      ).then(() => {
        this.handleResetCredentials()
      }).catch(() => {
        // 用户取消操作
      })
    },
    async handleResetCredentials() {
      this.loadingCredentials = true
      try {
        const response = await resetApiCredentials()
        if (response.success) {
          ElMessage.success('API凭证重置成功，即将退出登录')
          // 清除本地凭证信息
          this.apiCredentials = null
          this.showSecret = false
          // 延迟1秒后退出登录
          setTimeout(async () => {
            await logout()
            this.$router.push('/login')
          }, 1000)
        } else {
          ElMessage.error(response.msg || '重置API凭证失败')
        }
      } catch (error) {
        console.error('重置API凭证失败:', error)
        ElMessage.error('重置API凭证失败，请稍后重试')
      } finally {
        this.loadingCredentials = false
      }
    },
    async copyToClipboard(text, label) {
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(`${label} 已复制到剪贴板`)
      } catch (error) {
        // 降级方案：使用 textarea
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          ElMessage.success(`${label} 已复制到剪贴板`)
        } catch (err) {
          ElMessage.error('复制失败，请手动复制')
        }
        document.body.removeChild(textarea)
      }
    }
  },
  metaInfo() {
    return {
      title: this.$t('titles.apiDocs')
    }
  }
}
</script>

<style scoped>
.api-docs {
  padding: 40px 0 80px;
}

.docs-header {
  text-align: center;
  margin-bottom: 40px;
}

.docs-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--text-primary);
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.docs-subtitle {
  font-size: 18px;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
}

.docs-card {
  padding: 40px;
}

.docs-section {
  margin-bottom: 48px;
}

.docs-section:last-child {
  margin-bottom: 0;
}

.docs-section h2 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 24px;
  color: var(--text-primary);
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
}

.notice-box {
  background-color: rgba(68, 97, 242, 0.05);
  border-left: 4px solid var(--primary-color);
  padding: 20px;
  border-radius: var(--radius-sm);
}

.notice-box p {
  margin-bottom: 12px;
}

.notice-box ul {
  list-style-type: disc;
  padding-left: 20px;
}

.notice-box li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.api-details {
  margin-bottom: 24px;
}

.api-method {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  background-color: rgba(15, 23, 42, 0.02);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  border: 1px solid var(--border-color);
}

.method {
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  margin-right: 12px;
  font-size: 14px;
}

.method-get {
  background-color: #D1FADF;
  color: #047857;
}

.method-post {
  background-color: #FFEDD5;
  color: #C2410C;
}

.endpoint {
  font-family: monospace;
  color: var(--text-primary);
  font-size: 14px;
}

.api-details h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 24px 0 12px;
  color: var(--text-primary);
}

.table-container {
  overflow-x: auto;
  margin-bottom: 16px;
}

.api-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.api-table th, .api-table td {
  padding: 12px 16px;
  text-align: left;
  border: 1px solid var(--border-color);
}

.api-table th {
  background-color: rgba(15, 23, 42, 0.02);
  font-weight: 600;
}

.code-block {
  background-color: rgba(15, 23, 42, 0.05);
  border-radius: var(--radius-sm);
  padding: 16px;
  overflow-x: auto;
  margin-bottom: 16px;
}

.code-block-large {
  max-height: 300px;
  overflow-y: auto;
}

.code-block pre {
  margin: 0;
}

.code-block code {
  font-family: monospace;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.6;
}

.api-notes {
  background-color: rgba(255, 247, 237, 0.7);
  border-left: 4px solid #F59E0B;
  padding: 16px;
  border-radius: var(--radius-sm);
  margin-top: 24px;
}

.api-notes strong {
  color: #92400E;
  display: block;
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .docs-card {
    padding: 24px;
  }
  
  .docs-title {
    font-size: 28px;
  }
  
  .api-method {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .method {
    margin-bottom: 8px;
  }
}

/* 代码标签页样式 */
.code-tabs {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.tab-header {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  background-color: rgba(15, 23, 42, 0.02);
}

.tab-btn {
  padding: 12px 24px;
  background: none;
  border: none;
  border-right: 1px solid var(--border-color);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background-color: rgba(15, 23, 42, 0.05);
}

.tab-btn.active {
  background-color: #fff;
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
  margin-bottom: -1px;
}

.tab-content {
  padding: 0;
}

.tab-content .code-block {
  border-radius: 0;
  margin-bottom: 0;
}

/* API凭证样式 */
.credentials-container {
  margin-top: 20px;
}

.access-settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-top: 20px;
  align-items: start;
}

.access-panel {
  min-width: 0;
  background: #fff;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 20px;
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.panel-heading h3 {
  margin: 0 0 6px;
  color: var(--text-primary);
  font-size: 18px;
}

.panel-heading p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.status-badge {
  flex: none;
  padding: 5px 9px;
  border-radius: 999px;
  background: #ecfdf3;
  color: #067647;
  font-size: 12px;
  font-weight: 600;
}

.current-ip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding: 12px;
  border-radius: var(--radius-sm);
  background: #f8fafc;
  border: 1px solid var(--border-color);
}

.current-ip-row > div {
  min-width: 0;
}

.current-ip-row code {
  display: block;
  margin-top: 4px;
  word-break: break-all;
}

.field-caption {
  display: block;
  color: var(--text-secondary);
  font-size: 12px;
}

.credentials-actions {
  margin-top: 24px;
}

.whitelist-form {
  margin-top: 0;
}

.whitelist-form label {
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
  font-size: 14px;
}

.whitelist-form textarea {
  width: 100%;
  resize: vertical;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  font-size: 14px;
  color: var(--text-primary);
  background: #fff;
  box-sizing: border-box;
}

.whitelist-form textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(68, 97, 242, 0.12);
}

.whitelist-form textarea[aria-invalid="true"] {
  border-color: #d92d20;
}

.whitelist-form .field-error {
  color: #b42318;
}

.whitelist-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.whitelist-actions .btn {
  min-height: 44px;
  padding: 10px 20px;
  font-size: 14px;
}

.whitelist-actions .btn-secondary {
  background: #fff;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.whitelist-actions .btn-secondary:hover {
  background: var(--primary-color);
  color: #fff;
}

.whitelist-actions .btn-secondary:disabled {
  background: #f5f7fa;
  border-color: var(--border-color);
  color: var(--text-secondary);
  cursor: not-allowed;
}

.whitelist-form p {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.credentials-actions .btn {
  min-height: 44px;
  padding: 12px 32px;
  font-size: 16px;
}

.credentials-display {
  margin-top: 20px;
}

.credential-item {
  background: #f8fafc;
  border-radius: var(--radius-sm);
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid var(--border-color);
}

.credential-item:last-of-type {
  margin-bottom: 20px;
}

.credential-item label {
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
  font-size: 14px;
}

.credential-value {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  padding: 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
}

.credential-value code {
  flex: 1;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #1e293b;
  word-break: break-all;
  padding: 0;
  background: transparent;
}

.btn-copy,
.btn-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: #fff;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-secondary);
}

.btn-copy:hover,
.btn-toggle:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(68, 97, 242, 0.3);
}

.btn-copy:active,
.btn-toggle:active {
  transform: translateY(0);
}

.credentials-info {
  background: #fff;
  border-radius: var(--radius-sm);
  padding: 16px;
  margin-bottom: 16px;
}

.credentials-info .info-text {
  margin: 8px 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.credentials-info .info-text strong {
  color: var(--text-primary);
  margin-right: 8px;
}

.api-notes.warning {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 16px;
  border-radius: var(--radius-sm);
  margin-top: 16px;
}

.api-notes.warning strong {
  color: #856404;
  display: block;
  margin-bottom: 8px;
}

.api-notes.warning p {
  color: #856404;
  margin: 0;
  line-height: 1.6;
}

.danger-zone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #fecdca;
}

.danger-zone strong {
  color: #b42318;
  font-size: 14px;
}

.danger-zone p {
  margin: 4px 0 0;
  color: var(--text-secondary);
  font-size: 12px;
}

.danger-zone .btn-danger {
  flex: none;
  min-height: 44px;
  background: #d92d20;
  border: 1px solid #d92d20;
  color: #fff;
}

.danger-zone .btn-danger:hover {
  background: #b42318;
}

.danger-zone .btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .access-settings-grid {
    grid-template-columns: 1fr;
  }

  .current-ip-row,
  .danger-zone {
    align-items: stretch;
    flex-direction: column;
  }

  .current-ip-row .btn,
  .danger-zone .btn {
    width: 100%;
  }

  .credentials-display {
    margin-top: 16px;
  }

  .credential-value {
    flex-direction: column;
    gap: 12px;
  }

  .credential-value code {
    font-size: 12px;
  }

  .btn-copy,
  .btn-toggle {
    width: 100%;
    justify-content: center;
  }
}
</style>
